/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import axios from 'axios';

// ═══════════════════════════════════════════════════════════════════════════
// API Configuration
// ═══════════════════════════════════════════════════════════════════════════

// Production backend URL (Render)

/*
const PRODUCTION_API_BASE = 'https://skilldelivery-platform.onrender.com';

// Development backend URL (local)
const DEVELOPMENT_API_BASE = 'http://localhost:5000';

// Determine which URL to use
const isProduction = typeof window !== 'undefined' && 
                     window.location.hostname !== 'localhost' &&
                     window.location.hostname !== '127.0.0.1';

const API_BASE_URL = isProduction ? PRODUCTION_API_BASE : DEVELOPMENT_API_BASE;

*/

// WITH THIS:
const PRODUCTION_API_BASE = process.env.REACT_APP_ADMIN_API_BASE_URL 
                          || 'https://skilldelivery-platform.onrender.com';
const DEVELOPMENT_API_BASE = 'http://localhost:5000';

const isProduction = typeof window !== 'undefined' && 
                     window.location.hostname !== 'localhost' &&
                     window.location.hostname !== '127.0.0.1';

const API_BASE_URL = isProduction ? PRODUCTION_API_BASE : DEVELOPMENT_API_BASE;

console.log('[AdminAPI] Environment:', isProduction ? 'PRODUCTION' : 'DEVELOPMENT');
console.log('[AdminAPI] Base URL:', API_BASE_URL);

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - Add auth token to requests
// Request interceptor - Add auth token and 2FA session to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminAccessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add 2FA session token if available
    const twoFASession = localStorage.getItem('admin2FASession');
    if (twoFASession) {
      config.headers['x-2fa-session'] = twoFASession;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token expiration and password expiry
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ═══════════════════════════════════════════════════════════════
    // 2FA REQUIRED - DO NOT REDIRECT, let component handle
    // ═══════════════════════════════════════════════════════════════
    const errorCode = error.response?.data?.code;
    const errorMessage = error.response?.data?.error;

    if (errorCode === '2FA_REQUIRED' ||
        errorCode === '2FA_NOT_CONFIGURED' ||
        errorCode === '2FA_INVALID' ||
        errorMessage === 'TOTP_REQUIRED' ||
        error.response?.data?.requires2FA ||
        error.response?.data?.requires2FASetup) {
      // Return error to component for 2FA handling - DON'T redirect
      console.log('[AdminAPI] 2FA required/invalid - returning to component for handling');
      return Promise.reject(error);
    }

    // ═══════════════════════════════════════════════════════════════
    // PASSWORD EXPIRY HANDLING (NEW)
    // ═══════════════════════════════════════════════════════════════
    if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_EXPIRED') {
      // Store temporary token for password change
      if (error.response.data.temporaryToken) {
        localStorage.setItem('adminTempToken', error.response.data.temporaryToken);
      }
      // Redirect to admin password change
      window.location.href = '/admin/change-password?expired=true';
      return Promise.reject(error);
    }

    // ═══════════════════════════════════════════════════════════════
    // ACCOUNT LOCKOUT HANDLING (NEW)
    // ═══════════════════════════════════════════════════════════════
    if (error.response?.status === 423 && error.response?.data?.code === 'ACCOUNT_LOCKED') {
      // Let the calling component handle this
      return Promise.reject(error);
    }

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('adminRefreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/admin/auth/refresh`, {
            refreshToken
          });

          const { accessToken } = response.data.data;
          localStorage.setItem('adminAccessToken', accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('adminAccessToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminTempToken');
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Admin API Service
 */
const adminApi = {
  // ========================================
  // Generic Request Method
  // ========================================

  /**
   * Generic request method for custom API calls
   */
  request: async (endpoint, options = {}) => {
    const config = {
      method: options.method || 'GET',
      ...options
    };

    // Handle URL construction
    const url = endpoint.startsWith('/api/') ? endpoint : `/api${endpoint}`;

    // Add session ID header
    const sessionId = localStorage.getItem('adminSessionId');
    if (sessionId) {
      config.headers = {
        ...config.headers,
        'X-Admin-Session-Id': sessionId
      };
    }

    try {
      let response;
      if (config.method === 'GET') {
        response = await apiClient.get(url, config);
      } else if (config.method === 'POST') {
        response = await apiClient.post(url, config.body ? JSON.parse(config.body) : {}, config);
      } else if (config.method === 'PUT') {
        response = await apiClient.put(url, config.body ? JSON.parse(config.body) : {}, config);
      } else if (config.method === 'PATCH') {
        response = await apiClient.patch(url, config.body ? JSON.parse(config.body) : {}, config);
      } else if (config.method === 'DELETE') {
         response = await apiClient.delete(url, {
    ...config,
    data: config.body ? JSON.parse(config.body) : {}
  });
}
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ========================================
  // Authentication
  // ========================================

  /**
   * Login - Step 1 (may require 2FA)
   */
  login: async (username, password, captchaToken) => {
    const body = { username, password };
    // Backward-compatible: if the caller doesn't pass a token (e.g. dev
    // with CAPTCHA_ENABLED=false), the server accepts empty. Field name
    // matches the public form + web login.
    if (captchaToken) body['cf-turnstile-response'] = captchaToken;
    const response = await apiClient.post('/api/admin/auth/login', body);
    return response.data;
  },

  /**
   * Verify 2FA - Step 2 (complete login with 2FA code)
   */
  verify2FA: async (tempToken, code, isBackupCode = false) => {
    const response = await apiClient.post('/api/admin/auth/verify-2fa', {
      tempToken,
      code,
      isBackupCode
    });
    return response.data;
  },

  /**
   * Logout
   */
  logout: async () => {
    const response = await apiClient.post('/api/admin/auth/logout');
    return response.data;
  },

  /**
   * Verify session
   */
  verifySession: async () => {
    const response = await apiClient.get('/api/admin/auth/verify');
    return response.data;
  },

  // ========================================
  // Password Management (NEW)
  // ========================================

  /**
   * Get password policy rules
   */
  getPasswordPolicy: async () => {
    const response = await apiClient.get('/api/admin/auth/password-policy');
    return response.data;
  },

  /**
   * Validate password against policy (client-side helper)
   */
  validatePassword: async (password, context = {}) => {
    const response = await apiClient.post('/api/admin/auth/validate-password', {
      password,
      ...context
    });
    return response.data;
  },

  /**
   * Change password
   */
  changePassword: async (currentPassword, newPassword) => {
    // Check if using temp token (expired password flow)
    const tempToken = localStorage.getItem('adminTempToken');
    const config = {};
    
    if (tempToken) {
      config.headers = { Authorization: `Bearer ${tempToken}` };
    }

    const response = await apiClient.post('/api/admin/auth/change-password', {
      currentPassword,
      newPassword
    }, config);

    // Clear temp token on success
    if (response.data.success) {
      localStorage.removeItem('adminTempToken');
    }

    return response.data;
  },

  /**
   * Request password reset (forgot password)
   */
  forgotPassword: async (email) => {
    const response = await apiClient.post('/api/admin/auth/forgot-password', {
      email
    });
    return response.data;
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/api/admin/auth/reset-password', {
      token,
      newPassword
    });
    return response.data;
  },

  // ========================================
  // Two-Factor Authentication (2FA)
  // ========================================

  /**
   * Get 2FA status for current user
   */
  get2FAStatus: async () => {
    const response = await apiClient.get('/api/admin/auth/2fa/status');
    return response.data;
  },

  /**
   * Initiate 2FA setup - returns QR code
   */
  setup2FA: async () => {
    const response = await apiClient.post('/api/admin/auth/2fa/setup');
    return response.data;
  },

  /**
   * Enable 2FA with verification code
   */
  enable2FA: async (code) => {
    const response = await apiClient.post('/api/admin/auth/2fa/enable', { code });
    return response.data;
  },

  /**
   * Disable 2FA (requires password)
   */
  disable2FA: async (password) => {
    const response = await apiClient.post('/api/admin/auth/2fa/disable', { password });
    return response.data;
  },

  /**
   * Regenerate backup codes (requires password)
   */
  regenerateBackupCodes: async (password) => {
    const response = await apiClient.post('/api/admin/auth/2fa/regenerate-backup-codes', { password });
    return response.data;
  },

  // ========================================
  // Training Configs
  // ========================================

  /**
   * Get training configs for a domain
   */
  getTrainingConfigs: async (domain) => {
    const response = await apiClient.get(`/api/admin/training/configs/domain/${domain}`);
    return response.data;
  },

  // Training Jobs
  // ========================================

  /**
   * Submit training job
   */
  submitTrainingJob: async (jobData) => {
    const response = await apiClient.post('/api/admin/training/submit', jobData);
    return response.data;
  },

  /**
   * Get all training jobs
   */
  getTrainingJobs: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.domain) params.append('domain', filters.domain);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await apiClient.get(`/api/admin/training/jobs?${params}`);
    return response.data;
  },

  /**
   * Get single training job
   */
  getTrainingJob: async (jobId) => {
    const response = await apiClient.get(`/api/admin/training/jobs/${jobId}?_t=${Date.now()}`);
    return response.data;
  },

  /**
   * Cancel training job
   */
  cancelTrainingJob: async (jobId) => {
    const response = await apiClient.post(`/api/admin/training/jobs/${jobId}/cancel`);
    return response.data;
  },

  /**
   * Retry failed training job
   */
  retryTrainingJob: async (jobId) => {
    const response = await apiClient.post(`/api/admin/training/jobs/${jobId}/retry`);
    return response.data;
  },

  /**
   * Mark training job as complete
   */
  completeTrainingJob: async (jobId, results) => {
    const response = await apiClient.post(`/api/admin/training/jobs/${jobId}/complete`, results);
    return response.data;
  },

  // ========================================
  // Adapters
  // ========================================

  /**
   * Get all adapters
   */
  getAdapters: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.domain) params.append('domain', filters.domain);
    if (filters.active !== undefined) params.append('active', filters.active);

    const response = await apiClient.get(`/api/admin/training/adapters?${params}`);
    return response.data;
  },

  /**
   * Get single adapter
   */
  getAdapter: async (adapterId) => {
    const response = await apiClient.get(`/api/admin/training/adapters/${adapterId}`);
    return response.data;
  },

  /**
   * Activate adapter
   */
  activateAdapter: async (adapterId) => {
    const response = await apiClient.post(`/api/admin/training/adapters/${adapterId}/activate`);
    return response.data;
  },

  /**
   * Deactivate adapter
   */
  deactivateAdapter: async (adapterId) => {
    const response = await apiClient.post(`/api/admin/training/adapters/${adapterId}/deactivate`);
    return response.data;
  },

  /**
   * Sync adapters from filesystem to database
   */
  syncAdapters: async () => {
    const response = await apiClient.post('/api/admin/training/adapters/sync');
    return response.data;
  },

  // ========================================
  // System & Stats
  // ========================================

  /**
   * Get system health
   */
  getSystemHealth: async () => {
    const response = await apiClient.get('/api/health');
    return response.data;
  },

  /**
   * Get training statistics
   */
  getTrainingStats: async () => {
    const response = await apiClient.get('/api/admin/training/stats');
    return response.data;
  },

  // ========================================
  // System Configuration
  // ========================================

  /**
   * Get all system configurations
   */
  getSystemConfigs: async () => {
    const response = await apiClient.get('/api/admin/system-config');
    return response.data;
  },

  /**
   * Get configs by category
   */
  getSystemConfigsByCategory: async (category) => {
    const response = await apiClient.get(`/api/admin/system-config/category/${category}`);
    return response.data;
  },

  /**
   * Get processing-related configs (convenience)
   */
  getProcessingConfig: async () => {
    const response = await apiClient.get('/api/admin/system-config/processing');
    return response.data;
  },

  /**
   * Update a single config value
   */
  updateSystemConfig: async (key, value, reason = '') => {
    const response = await apiClient.put(`/api/admin/system-config/key/${key}`, {
      value,
      reason
    });
    return response.data;
  },

  /**
   * Update multiple configs at once
   */
  updateSystemConfigsBulk: async (updates, reason = '') => {
    const response = await apiClient.put('/api/admin/system-config/bulk', {
      updates,
      reason
    });
    return response.data;
  },

  /**
   * Reset a config to its default value
   */
  resetSystemConfig: async (key) => {
    const response = await apiClient.post(`/api/admin/system-config/reset/${key}`);
    return response.data;
  },

  /**
   * Invalidate config cache
   */
  invalidateConfigCache: async (key = null) => {
    const response = await apiClient.post('/api/admin/system-config/cache/invalidate', { key });
    return response.data;
  },

  // ========================================
  // File Upload
  // ========================================

  /**
   * Upload training data file
   */
  uploadTrainingData: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/api/admin/training/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      }
    });

    return response.data;
  },

  /**
   * Upload multiple files and combine them into single training file
   */
  prepareData: async (files, removeDuplicates = true, onProgress) => {
    const formData = new FormData();

    files.forEach(file => {
      formData.append('files', file);
    });

    formData.append('removeDuplicates', removeDuplicates.toString());

    const response = await apiClient.post('/api/admin/training/prepare-data', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      }
    });

    return response.data;
  },

  // ========================================
  // Training Datasets (Library)
  // ========================================

  /**
   * Get training datasets for library selection
   */
  getTrainingDatasets: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.domain) params.append('domain', filters.domain);
    if (filters.status) params.append('status', filters.status);
    
    const response = await apiClient.get(`/api/admin/training/datasets?${params.toString()}`);
    return response.data;
  },

  /**
   * Store uploaded training file to library for future use
   */
  storeTrainingDataset: async (data) => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('domain', data.domain);
    if (data.name) formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    
    const response = await apiClient.post('/api/admin/training/datasets/store', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // ========================================
  // Dataset Management
  // ========================================

  /**
   * Get all datasets
   */
  getDatasets: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.domain) params.append('domain', filters.domain);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const response = await apiClient.get(`/api/admin/datasets?${params}`);
    return response.data;
  },

  /**
   * Get single dataset
   */
  getDataset: async (datasetId) => {
    const response = await apiClient.get(`/api/admin/datasets/${datasetId}`);
    return response.data;
  },

  /**
   * Upload dataset
   */
  uploadDataset: async (file, metadata, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', metadata.name);
    formData.append('domain', metadata.domain);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.subDomain) formData.append('subDomain', metadata.subDomain);
    if (metadata.version) formData.append('version', metadata.version);
    if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));

    const response = await apiClient.post('/api/admin/datasets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      }
    });

    return response.data;
  },

  /**
   * Update dataset metadata
   */
  updateDataset: async (datasetId, updates) => {
    const response = await apiClient.put(`/api/admin/datasets/${datasetId}`, updates);
    return response.data;
  },

  /**
   * Archive dataset
   */
  archiveDataset: async (datasetId) => {
    const response = await apiClient.post(`/api/admin/datasets/${datasetId}/archive`);
    return response.data;
  },

  /**
   * Delete dataset
   */
  deleteDataset: async (datasetId) => {
    const response = await apiClient.delete(`/api/admin/datasets/${datasetId}`);
    return response.data;
  },

  /**
   * Download dataset
   */
  downloadDataset: async (datasetId) => {
    const response = await apiClient.get(`/api/admin/datasets/${datasetId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // ========================================
  // Adapter Upload (Modal via HuggingFace)
  // ========================================

  /**
   * Get adapter upload service configuration
   */
  getAdapterUploadConfig: async () => {
    const response = await apiClient.get('/api/admin/adapter-upload/config');
    return response.data;
  },

  /**
   * List local adapters available for upload
   */
  getLocalAdapters: async (vertical = null) => {
    const url = vertical
      ? `/api/admin/adapter-upload/adapters/${vertical}`
      : '/api/admin/adapter-upload/adapters';
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get specific adapter details
   */
  getLocalAdapterDetails: async (vertical, name) => {
    const response = await apiClient.get(`/api/admin/adapter-upload/adapters/${vertical}/${name}`);
    return response.data;
  },

  /**
   * Upload adapter to HuggingFace Hub only
   */
  uploadAdapterToHF: async ({ vertical, adapterName, version, description }) => {
    const response = await apiClient.post('/api/admin/adapter-upload/upload', {
      vertical,
      adapterName,
      version,
      description
    });
    return response.data;
  },

  /**
   * Sync adapter from HuggingFace to Modal volume
   */
  syncAdapterToModal: async ({ vertical, adapterName, hfPath }) => {
    const response = await apiClient.post('/api/admin/adapter-upload/sync', {
      vertical,
      adapterName,
      hfPath
    });
    return response.data;
  },

  /**
   * Full upload pipeline: Local → HuggingFace → Modal
   */
  uploadAdapterFull: async ({ vertical, adapterName, version, description, skipModal }) => {
    const response = await apiClient.post('/api/admin/adapter-upload/upload-full', {
      vertical,
      adapterName,
      version,
      description,
      skipModal
    });
    return response.data;
  },

  /**
   * Get upload history for an adapter
   */
  getAdapterUploadHistory: async (vertical, name) => {
    const response = await apiClient.get(`/api/admin/adapter-upload/history/${vertical}/${name}`);
    return response.data;
  },

  // ========================================
  // Adapter Benchmarking
  // ========================================

  /**
   * Get all benchmarks
   */
  getBenchmarks: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.adapterId) params.append('adapterId', filters.adapterId);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await apiClient.get(`/api/admin/benchmarks?${params}`);
    return response.data;
  },

  /**
   * Get single benchmark
   */
  getBenchmark: async (benchmarkId) => {
    const response = await apiClient.get(`/api/admin/benchmarks/${benchmarkId}`);
    return response.data;
  },

  /**
   * Run benchmark test
   */
  runBenchmark: async (adapterId, testSuite, config) => {
    const response = await apiClient.post('/api/admin/benchmarks/run', {
      adapterId,
      testSuite,
      config
    });
    return response.data;
  },

  /**
   * Get latest benchmark for adapter
   */
  getAdapterLatestBenchmark: async (adapterId) => {
    const response = await apiClient.get(`/api/admin/benchmarks/adapter/${adapterId}/latest`);
    return response.data;
  },

  /**
   * Get benchmark history for adapter
   */
  getAdapterBenchmarkHistory: async (adapterId, limit = 20) => {
    const response = await apiClient.get(`/api/admin/benchmarks/adapter/${adapterId}/history?limit=${limit}`);
    return response.data;
  },

  // ========================================
  // Generic HTTP Methods (for dynamic features like Prompt Management)
  // ========================================

  /**
   * Generic GET request
   */
  get: async (url, config = {}) => {
    const response = await apiClient.get(url, config);
    return response;
  },

  /**
   * Generic POST request
   */
  post: async (url, data = {}, config = {}) => {
    const response = await apiClient.post(url, data, config);
    return response;
  },

  /**
   * Generic PUT request
   */
  put: async (url, data = {}, config = {}) => {
    const response = await apiClient.put(url, data, config);
    return response;
  },

  /**
   * Generic PATCH request
   */
  patch: async (url, data = {}, config = {}) => {
    const response = await apiClient.patch(url, data, config);
    return response;
  },

  /**
   * Generic DELETE request
   */
  delete: async (url, config = {}) => {
    const response = await apiClient.delete(url, config);
    return response;
  },

  // ========================================
  // Spine — per-vertical seed-question editor
  // ========================================

  listSpineSeedSets: async (domain) => {
    const params = new URLSearchParams();
    if (domain) params.append('domain', domain);
    const response = await apiClient.get(`/api/admin/spine/seeds?${params.toString()}`);
    return response.data;
  },

  getActiveSpineSeedSet: async (domain) => {
    const response = await apiClient.get(
      `/api/admin/spine/seeds/active?domain=${encodeURIComponent(domain)}`
    );
    return response.data;
  },

  createSpineSeedSet: async ({ name, domain, description, questions }) => {
    const response = await apiClient.post('/api/admin/spine/seeds', {
      name, domain, description, questions
    });
    return response.data;
  },

  updateSpineSeedSet: async (id, { name, description, questions }) => {
    const response = await apiClient.put(`/api/admin/spine/seeds/${id}`, {
      name, description, questions
    });
    return response.data;
  },

  activateSpineSeedSet: async (id) => {
    const response = await apiClient.post(`/api/admin/spine/seeds/${id}/activate`);
    return response.data;
  },

  testSpineSeedTopic: async ({ domain, topic }) => {
    const response = await apiClient.post('/api/admin/spine/seeds/test-topic', {
      domain, topic
    });
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// S0 Curriculum Gap Engine
// Backend: /api/admin/curriculum-gaps/* (permission: curriculum_gaps.manage)
// ═══════════════════════════════════════════════════════════════════════════

adminApi.listGapDomains = async () => {
  const response = await apiClient.get('/api/admin/curriculum-gaps/domains');
  return response.data;
};

adminApi.uploadSyllabus = async ({ file, institutionName, programmeName, domainId, extractedText }) => {
  const formData = new FormData();
  if (file) formData.append('file', file);
  formData.append('institutionName', institutionName);
  formData.append('programmeName', programmeName);
  formData.append('domainId', domainId);
  if (extractedText) formData.append('extractedText', extractedText);
  const response = await apiClient.post('/api/admin/curriculum-gaps/syllabi', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

adminApi.listSyllabi = async () => {
  const response = await apiClient.get('/api/admin/curriculum-gaps/syllabi');
  return response.data;
};

adminApi.getSyllabus = async (id) => {
  const response = await apiClient.get(`/api/admin/curriculum-gaps/syllabi/${id}`);
  return response.data;
};

adminApi.updateSyllabusUnits = async (id, units) => {
  const response = await apiClient.put(`/api/admin/curriculum-gaps/syllabi/${id}/units`, { units });
  return response.data;
};

adminApi.addSyllabusJd = async (syllabusId, { text, label }) => {
  const response = await apiClient.post(`/api/admin/curriculum-gaps/syllabi/${syllabusId}/jds`, { text, label });
  return response.data;
};

adminApi.listSyllabusJds = async (syllabusId) => {
  const response = await apiClient.get(`/api/admin/curriculum-gaps/syllabi/${syllabusId}/jds`);
  return response.data;
};

adminApi.startGapRun = async ({ syllabusId, jdIds }) => {
  const response = await apiClient.post('/api/admin/curriculum-gaps/runs', { syllabusId, jdIds });
  return response.data;
};

adminApi.listGapRuns = async (syllabusId) => {
  const q = syllabusId ? `?syllabusId=${encodeURIComponent(syllabusId)}` : '';
  const response = await apiClient.get(`/api/admin/curriculum-gaps/runs${q}`);
  return response.data;
};

adminApi.getGapRun = async (id) => {
  const response = await apiClient.get(`/api/admin/curriculum-gaps/runs/${id}`);
  return response.data;
};

adminApi.listGapRunCandidates = async (id, status) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const response = await apiClient.get(`/api/admin/curriculum-gaps/runs/${id}/candidates${q}`);
  return response.data;
};

adminApi.updateGapCandidate = async (id, { resolutionStatus, proposedCapabilityId, rejectionReason }) => {
  const response = await apiClient.patch(`/api/admin/curriculum-gaps/candidates/${id}`, {
    resolutionStatus, proposedCapabilityId, rejectionReason,
  });
  return response.data;
};

adminApi.getGapMap = async (runId) => {
  const response = await apiClient.get(`/api/admin/curriculum-gaps/runs/${runId}/gap-map`);
  return response.data;
};

export default adminApi;
